import { Caption } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useEffect, useState } from 'react';

import { CatalogCardTimedPlusDate } from '@/components/CatalogCard/components/CatalogCardDate/TimedPlusDate/CatalogCardTimedPlusDate';
import { useAppSelector } from '@/redux/hooks';
import {
    getCatalogEndTime,
    getCatalogIsCatalogOnly,
    getCatalogIsDone,
    getCatalogIsTimedPlus,
    getCatalogStartTime,
    getCatalogStatus,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { longFormatDate } from '@/utils/dates';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { shouldShowLive } from '@/utils/live';

type Props = {
    catalogId: number;
    className?: string;
    forceIsLive?: boolean;
};

export const CatalogCardDate = ({ catalogId, className, forceIsLive }: Props) => {
    const isCatalogDone = useAppSelector((state) => getCatalogIsDone(state, catalogId));
    const isCatalogTimedPlus = useAppSelector((state) => getCatalogIsTimedPlus(state, catalogId));

    const saleStartTs = useAppSelector((state) => getCatalogStartTime(state, catalogId)) || Date.now() / 1000;
    const saleEndTs = useAppSelector((state) => getCatalogEndTime(state, catalogId)) || Date.now() / 1000;
    const catalogStatus = useAppSelector((state) => getCatalogStatus(state, catalogId));
    const isCatalogOnly = useAppSelector((state) => getCatalogIsCatalogOnly(state, catalogId));
    const isLive = forceIsLive || shouldShowLive(catalogStatus, isCatalogOnly, saleStartTs);
    const formatMessage = useAppFormatMessage();

    const [saleStartDate, setSaleStarDate] = useState('');
    const [saleEndDate, setSaleEndDate] = useState('');

    useEffect(() => {
        setSaleStarDate(longFormatDate(saleStartTs));
        setSaleEndDate(longFormatDate(saleEndTs));
    }, [saleStartTs, saleEndTs]);

    return (
        <div
            className={cn('flex flex-row items-center justify-start pr-md', className)}
            data-testid="catalog-date"
        >
            {isCatalogDone ? (
                <Caption color="secondary">{saleEndDate}</Caption>
            ) : isCatalogTimedPlus ? (
                <CatalogCardTimedPlusDate catalogId={catalogId} />
            ) : isLive ? (
                <Caption
                    className="block"
                    color="secondary"
                    data-testid="timed-or-live-date"
                    suppressHydrationWarning
                >
                    {formatMessage(['catalogPage', 'catalogDate', 'started'], { values: { date: saleStartDate } })}
                </Caption>
            ) : (
                <Caption
                    className="block"
                    color="secondary"
                    data-testid="timed-or-live-date"
                    suppressHydrationWarning
                >
                    {formatMessage(['catalogPage', 'catalogDate', 'startsOn'], { values: { date: saleStartDate } })}
                </Caption>
            )}
        </div>
    );
};
